/**
 * SupplierForm
 *
 * The party (supplier / customer) form: its state, the ARES register lookups and the fields.
 * Shared by {@link SupplierModal} and the host app's full-page party editor so both behave
 * identically. Uses `useTranslation('modules.modals.supplier')` for all labels.
 */
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { isValidIco } from '@insio/validation';
import { VatPayerType } from '@insio/shared-types';
import { cn, toWebsiteHref } from '../../../utils';
import { Button } from '../../../components/Button';
import { EmailInput, Input, PhoneInput } from '../../../components/inputs';
import { Icon } from '../../../components/icons/Icon';
import { InputWrapper } from '../../../components/InputWrapper';
import { CountrySelector } from '../../../components/selects/CountrySelector';
import { Select } from '../../../components/selects/Select';
import { useTranslation } from '../../../providers/UIProvider';
import type { AddressFormState } from '../address/AddressForm';
import { AddressForm } from '../address/AddressForm';

export interface SupplierFormData {
  name: string;
  code: string;
  companyId: string;
  vatId: string;
  taxId: string;
  /** `''` when the VAT payer type is not specified. */
  vatPayerType: VatPayerType | '';
  email: string;
  phone: string;
  mobile: string;
  website: string;
  streetName: string;
  buildingNumber: string;
  city: string;
  postalCode: string;
  countryCode: string;
  isActive: boolean;
  isSupplier: boolean;
  isCustomer: boolean;
}

export interface SupplierData {
  id: string;
  name: string;
  code?: string | null;
  companyId?: string | null;
  vatId?: string | null;
  taxId?: string | null;
  vatPayerType?: VatPayerType | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  website?: string | null;
  isActive: boolean;
  isSupplier?: boolean | null;
  isCustomer?: boolean | null;
  mainAddress?: {
    streetName?: string | null;
    buildingNumber?: string | null;
    city?: string | null;
    postalCode?: string | null;
    countryCode?: string | null;
  } | null;
}

export interface AresLookupAddress {
  streetName?: string | null;
  buildingNumber?: string | null;
  city?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
}

/** Company data from an ARES lookup, provided by the host app. */
export interface AresLookupData {
  companyId: string;
  name: string;
  /** The subject's own DIČ, only where it differs from `vatId`. */
  taxId: string | null;
  /** DIČ the subject is registered for VAT under — the group one when that registration is active. */
  vatId?: string | null;
  address?: AresLookupAddress | null;
}

export const EMPTY_SUPPLIER_FORM: SupplierFormData = {
  name: '',
  code: '',
  companyId: '',
  vatId: '',
  taxId: '',
  vatPayerType: '',
  email: '',
  phone: '',
  mobile: '',
  website: '',
  streetName: '',
  buildingNumber: '',
  city: '',
  postalCode: '',
  countryCode: '',
  isActive: true,
  isSupplier: true,
  isCustomer: false
};

/** Accepts IČO or Czech DIČ — strips whitespace and the "CZ" prefix. */
function normalizeCompanyIdInput(value: string): string {
  return value.replace(/\s+/g, '').replace(/^cz/i, '');
}

/** DIČ and VAT ID as last written by a register lookup, to tell them from manual entries. */
interface RegistryFilled {
  taxId: string | null;
  vatId: string | null;
}

const NOTHING_FILLED: RegistryFilled = { taxId: null, vatId: null };

/**
 * The register returned no value for this field: a manually entered one survives, one an
 * earlier lookup wrote belongs to the previous IČO and is cleared.
 */
function withoutStale(current: string, lastFromRegistry: string | null): string {
  return current === lastFromRegistry ? '' : current;
}

/** A value the user typed, as opposed to an empty field or one an earlier lookup wrote. */
function isManual(value: string, lastFromRegistry: string | null): boolean {
  return value.trim() !== '' && value !== lastFromRegistry;
}

/**
 * The VAT ID and DIČ to show after a lookup: register values win; where the register has none,
 * a manual entry survives and a value an earlier lookup wrote for another IČO is dropped.
 */
function resolveTaxIds(
  result: AresLookupData | null,
  current: Pick<SupplierFormData, 'taxId' | 'vatId'>,
  filled: RegistryFilled
): { taxId: string; vatId: string; filled: RegistryFilled } {
  const registryVatId = result?.vatId ?? null;
  const registryTaxId = result?.taxId ?? null;

  return {
    vatId: registryVatId ?? withoutStale(current.vatId, filled.vatId),
    taxId: registryTaxId ?? withoutStale(current.taxId, filled.taxId),
    filled: { taxId: registryTaxId, vatId: registryVatId }
  };
}

/** Either tax identifier was typed by hand, so an opportunistic lookup leaves both alone. */
function hasManualTaxIds(
  current: Pick<SupplierFormData, 'taxId' | 'vatId'>,
  filled: RegistryFilled
): boolean {
  return isManual(current.vatId, filled.vatId) || isManual(current.taxId, filled.taxId);
}

export interface UseSupplierFormOptions {
  /** The party being edited; `null`/absent starts an empty create form. */
  supplier?: SupplierData | null;
  validateEmail?: (email: string) => boolean;
  /** Checks a filled-in website; pass it where the fields show mobile and website. */
  validateWebsite?: (website: string) => boolean;
  /**
   * ARES lookup by IČO. Resolves `null` when not found, rejects on service error.
   * When omitted, the ARES search field is hidden.
   */
  onAresLookup?: (ico: string) => Promise<AresLookupData | null>;
  /** While `false` the form does not re-bind — the modal uses it for its `open` flag. */
  active?: boolean;
}

export interface SupplierFormState {
  formData: SupplierFormData;
  setFormData: React.Dispatch<React.SetStateAction<SupplierFormData>>;
  errors: Partial<Record<keyof SupplierFormData, string>>;
  /** The first error to surface above the form, or `null`. */
  formError: string | null;
  validateForm: () => boolean;
  reset: () => void;
  onAresLookup?: (ico: string) => Promise<AresLookupData | null>;
  /** Internals the field set needs; not part of the public contract. */
  internal: {
    isCzOrUnknown: boolean;
    aresQuery: string;
    setAresQuery: (next: string) => void;
    aresLoading: boolean;
    aresError: string | null;
    setAresError: (next: string | null) => void;
    aresApplied: boolean;
    setAresApplied: (next: boolean) => void;
    handleAresApply: () => Promise<void>;
    fillTaxIdsFromRegistry: () => Promise<void>;
    vatIdLookupLoading: boolean;
    vatIdAutoFilled: boolean;
    setVatIdAutoFilled: (next: boolean) => void;
  };
}

/** Form state, validation and the ARES register lookups for a party. */
export function useSupplierForm({
  supplier,
  validateEmail,
  validateWebsite,
  onAresLookup,
  active = true
}: UseSupplierFormOptions): SupplierFormState {
  const t = useTranslation('modules.modals.supplier');
  const [formData, setFormData] = useState<SupplierFormData>(EMPTY_SUPPLIER_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof SupplierFormData, string>>>({});
  const [aresQuery, setAresQuery] = useState('');
  const [aresLoading, setAresLoading] = useState(false);
  const [aresError, setAresError] = useState<string | null>(null);
  const [aresApplied, setAresApplied] = useState(false);
  const [vatIdLookupLoading, setVatIdLookupLoading] = useState(false);
  const [vatIdAutoFilled, setVatIdAutoFilled] = useState(false);
  /** Last IČO looked up for the tax IDs, so an unchanged field does not query again. */
  const lastTaxIdLookupRef = useRef<string | null>(null);
  /** Bumped on every reset/lookup so a stale response cannot land in a rebound form. */
  const taxIdRequestRef = useRef(0);
  /** What the last lookup wrote, so a lookup for another IČO can replace it. */
  const registryFilledRef = useRef<RegistryFilled>(NOTHING_FILLED);
  /** Mirrors `formData` so an in-flight lookup can re-check it after awaiting. */
  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!active) return;
    if (supplier) {
      setFormData({
        name: supplier.name,
        code: supplier.code || '',
        companyId: supplier.companyId || '',
        vatId: supplier.vatId || '',
        taxId: supplier.taxId || '',
        vatPayerType: supplier.vatPayerType ?? '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        mobile: supplier.mobile || '',
        website: supplier.website || '',
        streetName: supplier.mainAddress?.streetName || '',
        buildingNumber: supplier.mainAddress?.buildingNumber || '',
        city: supplier.mainAddress?.city || '',
        postalCode: supplier.mainAddress?.postalCode || '',
        countryCode: (supplier.mainAddress?.countryCode || '').toUpperCase(),
        isActive: supplier.isActive,
        isSupplier: supplier.isSupplier ?? true,
        isCustomer: supplier.isCustomer ?? false
      });
    } else {
      setFormData(EMPTY_SUPPLIER_FORM);
    }
    setErrors({});
    setAresQuery(supplier?.companyId || '');
    setAresError(null);
    setAresLoading(false);
    setAresApplied(false);
    setVatIdLookupLoading(false);
    setVatIdAutoFilled(false);
    lastTaxIdLookupRef.current = null;
    taxIdRequestRef.current += 1;
    // Values bound from a saved partner count as manual — a lookup must not wipe them.
    registryFilledRef.current = NOTHING_FILLED;
  }, [active, supplier]);

  // IČO (companyId) is a Czech identifier — only mandatory for Czech partners.
  const isCzech = formData.countryCode === 'CZ';

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof SupplierFormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = t('errors.nameRequired');
    if (isCzech && !formData.companyId?.trim()) newErrors.companyId = t('errors.companyIdRequired');
    if (formData.email && validateEmail && !validateEmail(formData.email))
      newErrors.email = t('errors.emailInvalid');
    if (formData.website.trim() && validateWebsite && !validateWebsite(formData.website))
      newErrors.website = t('errors.websiteInvalid');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const reset = () => {
    setFormData(EMPTY_SUPPLIER_FORM);
    setErrors({});
  };

  const handleAresApply = async () => {
    if (aresLoading || !onAresLookup) return;
    const normalized = normalizeCompanyIdInput(aresQuery);
    if (!isValidIco(normalized)) {
      setAresError(t('errors.aresInvalidInput'));
      return;
    }
    setAresError(null);
    setAresApplied(false);
    setAresLoading(true);
    try {
      const result = await onAresLookup(normalized);
      // Remember a hit so the IČO field's own lookup does not repeat it; a miss stays retryable.
      lastTaxIdLookupRef.current = result ? normalized : null;
      if (!result) {
        setAresError(t('errors.aresNotFound'));
        return;
      }
      const {
        taxId: nextTaxId,
        vatId: nextVatId,
        filled: nextFilled
      } = resolveTaxIds(result, formDataRef.current, registryFilledRef.current);
      registryFilledRef.current = nextFilled;

      setFormData(prev => ({
        ...prev,
        name: result.name,
        companyId: result.companyId,
        taxId: nextTaxId,
        vatId: nextVatId,
        streetName: result.address?.streetName ?? '',
        buildingNumber: result.address?.buildingNumber ?? '',
        city: result.address?.city ?? '',
        postalCode: result.address?.postalCode ?? '',
        countryCode: 'CZ'
      }));
      setErrors({});
      setAresApplied(true);
    } catch {
      setAresError(t('errors.aresUnavailable'));
    } finally {
      setAresLoading(false);
    }
  };

  /**
   * Syncs the VAT ID — and a differing DIČ — with the register after the IČO field is left.
   * Opportunistic: never overwrites a manual entry, never reports errors.
   */
  const fillTaxIdsFromRegistry = async () => {
    // Values an earlier lookup wrote belong to the previous IČO, so they do not block a new one.
    if (
      !onAresLookup ||
      formData.countryCode !== 'CZ' ||
      hasManualTaxIds(formData, registryFilledRef.current)
    )
      return;

    const normalized = normalizeCompanyIdInput(formData.companyId);
    if (!isValidIco(normalized) || normalized === lastTaxIdLookupRef.current) return;

    lastTaxIdLookupRef.current = normalized;
    const requestId = ++taxIdRequestRef.current;
    setVatIdLookupLoading(true);
    try {
      const result = await onAresLookup(normalized);
      // The form may have been rebound for another partner, or a newer lookup started.
      if (requestId !== taxIdRequestRef.current) return;

      const current = formDataRef.current;
      // The user may have typed a tax ID while the request was in flight.
      if (hasManualTaxIds(current, registryFilledRef.current)) return;

      const {
        taxId: nextTaxId,
        vatId: nextVatId,
        filled: nextFilled
      } = resolveTaxIds(result, current, registryFilledRef.current);
      registryFilledRef.current = nextFilled;

      setFormData(prev => ({ ...prev, taxId: nextTaxId, vatId: nextVatId }));
      setVatIdAutoFilled(!!nextFilled.vatId);
    } catch {
      // A register outage must not block manual entry — allow a retry on the same IČO.
      lastTaxIdLookupRef.current = null;
    } finally {
      if (requestId === taxIdRequestRef.current) setVatIdLookupLoading(false);
    }
  };

  // The ARES lookup is offered for CZ / unknown-country partners only.
  const isCzOrUnknown = !formData.countryCode || formData.countryCode === 'CZ';

  return {
    formData,
    setFormData,
    errors,
    formError: Object.values(errors).find(Boolean) ?? null,
    validateForm,
    reset,
    onAresLookup,
    internal: {
      isCzOrUnknown,
      aresQuery,
      setAresQuery,
      aresLoading,
      aresError,
      setAresError,
      aresApplied,
      setAresApplied,
      handleAresApply,
      fillTaxIdsFromRegistry,
      vatIdLookupLoading,
      vatIdAutoFilled,
      setVatIdAutoFilled
    }
  };
}

export interface SupplierFormFieldsProps {
  form: SupplierFormState;
  /**
   * When `true` the "Active" checkbox is hidden and `isActive` is always `true`.
   * Use when creating a partner inline from invoice detail — a new partner
   * created in context should always be active.
   */
  hideIsActive?: boolean;
  /** Prefix for field element ids, so two forms can coexist on one page. */
  idPrefix?: string;
  /** Called when Enter is pressed in the ARES field, instead of submitting a surrounding form. */
  preventEnterSubmit?: boolean;
  /**
   * VAT payer type labels, translated by the host app.
   * When set, the select is shown under the VAT ID.
   */
  vatPayerTypeLabels?: Readonly<Record<VatPayerType, string>>;
  /** Adds mobile and website to the contact section. */
  showMobileAndWebsite?: boolean;
}

/** Order of the VAT payer type options: the enum's declaration order. */
const VAT_PAYER_TYPES: readonly VatPayerType[] = Object.values(VatPayerType);

interface VatPayerTypeFieldProps {
  id: string;
  labels: Readonly<Record<VatPayerType, string>>;
  value: VatPayerType | '';
  onChange: (next: VatPayerType | '') => void;
}

/** The VAT payer type select, with an empty "not specified" option. */
function VatPayerTypeField({ id, labels, value, onChange }: VatPayerTypeFieldProps) {
  const t = useTranslation('modules.modals.supplier');
  const options = VAT_PAYER_TYPES.map(type => ({ value: type, label: labels[type] }));

  return (
    <InputWrapper label={t('fields.vatPayerType')} htmlFor={id}>
      <Select
        id={id}
        ariaLabel={t('fields.vatPayerType')}
        options={options}
        value={value}
        UnselectedOption={t('placeholders.vatPayerTypeNone')}
        onChange={next => {
          const picked = Array.isArray(next) ? next[0] : next;
          onChange(VAT_PAYER_TYPES.find(type => type === picked) ?? '');
        }}
      />
    </InputWrapper>
  );
}

/** Contact field lengths the API accepts. */
const MOBILE_MAX_LENGTH = 50;
const WEBSITE_MAX_LENGTH = 255;

/** The party fields themselves; drive them with {@link useSupplierForm}. */
export function SupplierFormFields({
  form,
  hideIsActive = false,
  idPrefix = 'supplier',
  preventEnterSubmit = true,
  showMobileAndWebsite = false,
  vatPayerTypeLabels
}: SupplierFormFieldsProps) {
  const t = useTranslation('modules.modals.supplier');
  const { formData, setFormData, errors, onAresLookup, internal } = form;
  const isCzech = formData.countryCode === 'CZ';
  const websiteHref = showMobileAndWebsite ? toWebsiteHref(formData.website) : null;

  const emailField = (
    <InputWrapper
      label={t('fields.email')}
      htmlFor={`${idPrefix}-email`}
      isError={!!errors.email}
      errorText={errors.email}
    >
      <EmailInput
        id={`${idPrefix}-email`}
        value={formData.email}
        onChange={e => setFormData({ ...formData, email: e.target.value })}
        placeholder={t('placeholders.email')}
      />
    </InputWrapper>
  );
  const phoneField = (
    <InputWrapper label={t('fields.phone')} htmlFor={`${idPrefix}-phone`}>
      <PhoneInput
        id={`${idPrefix}-phone`}
        value={formData.phone}
        onChange={e => setFormData({ ...formData, phone: e.target.value })}
        placeholder={t('placeholders.phone')}
      />
    </InputWrapper>
  );

  return (
    <>
      {/* ARES lookup — CZ only, hidden once a non-CZ country is selected */}
      {onAresLookup && internal.isCzOrUnknown && (
        <div className='bg-background-muted border border-border-base rounded-lg p-md'>
          <InputWrapper
            label={t('ares.label')}
            htmlFor={`${idPrefix}-ares-query`}
            isError={!!internal.aresError}
            errorText={internal.aresError ?? undefined}
          >
            {/* Own id so InputWrapper doesn't clone the label's id onto this div */}
            <div id={`${idPrefix}-ares-row`} className='flex gap-sm'>
              <Input
                id={`${idPrefix}-ares-query`}
                type='text'
                value={internal.aresQuery}
                onChange={e => {
                  internal.setAresQuery(e.target.value);
                  internal.setAresError(null);
                  internal.setAresApplied(false);
                }}
                onKeyDown={e => {
                  // Enter inside a surrounding <form> would submit the whole form.
                  if (e.key === 'Enter' && preventEnterSubmit) {
                    e.preventDefault();
                    void internal.handleAresApply();
                  }
                }}
                placeholder={t('ares.placeholder')}
                className='flex-1'
              />
              <Button
                type='button'
                variant='primary'
                className='h-10'
                onClick={() => void internal.handleAresApply()}
                disabled={internal.aresLoading || !internal.aresQuery.trim()}
              >
                {internal.aresLoading ? t('ares.loading') : t('ares.button')}
              </Button>
            </div>
          </InputWrapper>
          {internal.aresApplied && (
            <p role='status' className='text-sm text-success-text mt-xs'>
              {t('ares.applied')}
            </p>
          )}
        </div>
      )}

      {/* Name (2/3) + Code (1/3) on one row */}
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-md'>
        <InputWrapper
          label={t('fields.name')}
          htmlFor={`${idPrefix}-name`}
          required
          isError={!!errors.name}
          errorText={errors.name}
          className='sm:col-span-2'
        >
          <Input
            id={`${idPrefix}-name`}
            type='text'
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('placeholders.name')}
          />
        </InputWrapper>
        <InputWrapper label={t('fields.code')} htmlFor={`${idPrefix}-code`}>
          <Input
            id={`${idPrefix}-code`}
            type='text'
            value={formData.code}
            onChange={e => setFormData({ ...formData, code: e.target.value })}
            placeholder={t('placeholders.code')}
          />
        </InputWrapper>
      </div>

      {/* Country — placed early because it controls field visibility */}
      <InputWrapper label={t('fields.countryCode')} htmlFor={`${idPrefix}-countryCode`}>
        <CountrySelector
          id={`${idPrefix}-countryCode`}
          value={formData.countryCode}
          onChange={code => setFormData({ ...formData, countryCode: code })}
          unselectedOptionLabel={t('placeholders.countryCodeNone')}
        />
      </InputWrapper>

      {/* All fields below are only shown once a country is selected or auto-filled */}
      {formData.countryCode && (
        <>
          {/* IČO, DIČ pro DPH, DIČ */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-md'>
            <InputWrapper
              label={t('fields.companyId')}
              htmlFor={`${idPrefix}-companyId`}
              required={isCzech}
              isError={!!errors.companyId}
              errorText={errors.companyId}
            >
              <Input
                id={`${idPrefix}-companyId`}
                type='text'
                value={formData.companyId}
                onChange={e => setFormData({ ...formData, companyId: e.target.value })}
                onBlur={() => void internal.fillTaxIdsFromRegistry()}
                placeholder={t('placeholders.companyId')}
              />
            </InputWrapper>
            <InputWrapper
              label={t('fields.vatId')}
              htmlFor={`${idPrefix}-vatId`}
              hint={t('hints.vatId')}
            >
              <Input
                id={`${idPrefix}-vatId`}
                type='text'
                value={formData.vatId}
                onChange={e => {
                  setFormData({ ...formData, vatId: e.target.value });
                  internal.setVatIdAutoFilled(false);
                }}
                placeholder={t('placeholders.vatId')}
                aria-describedby={`${idPrefix}-vatId-registry-status`}
              />
              {/* Own id so InputWrapper doesn't clone the input's id onto this paragraph.
                  Always mounted so the live region exists before it gets content. */}
              <p
                id={`${idPrefix}-vatId-registry-status`}
                role='status'
                className={cn(
                  'text-sm',
                  internal.vatIdLookupLoading && 'text-foreground-muted mt-xs',
                  !internal.vatIdLookupLoading &&
                    internal.vatIdAutoFilled &&
                    'text-success-text mt-xs'
                )}
              >
                {internal.vatIdLookupLoading
                  ? t('ares.vatIdLoading')
                  : internal.vatIdAutoFilled
                    ? t('ares.vatIdFilled')
                    : null}
              </p>
            </InputWrapper>
            <InputWrapper label={t('fields.taxId')} htmlFor={`${idPrefix}-taxId`}>
              <Input
                id={`${idPrefix}-taxId`}
                type='text'
                value={formData.taxId}
                onChange={e => setFormData({ ...formData, taxId: e.target.value })}
                placeholder={t('placeholders.taxId')}
              />
            </InputWrapper>
          </div>

          {vatPayerTypeLabels && (
            <VatPayerTypeField
              id={`${idPrefix}-vatPayerType`}
              labels={vatPayerTypeLabels}
              value={formData.vatPayerType}
              onChange={next => setFormData(prev => ({ ...prev, vatPayerType: next }))}
            />
          )}

          {/* Address — country is already chosen above, so hideCountry avoids duplication */}
          <div className='border-t border-border-base pt-md'>
            <h3 className='text-base sm:text-lg font-semibold text-foreground-base mb-md'>
              {t('sections.address')}
            </h3>
            <AddressForm
              value={{
                streetName: formData.streetName,
                buildingNumber: formData.buildingNumber,
                city: formData.city,
                postalCode: formData.postalCode,
                countryCode: formData.countryCode
              }}
              onFieldChange={(field: keyof AddressFormState, next: string) =>
                setFormData(prev => ({ ...prev, [field]: next }))
              }
              hideCountry
              autoFocusStreet={false}
            />
          </div>

          {/* Contact */}
          <div className='border-t border-border-base pt-md'>
            <h3 className='text-base sm:text-lg font-semibold text-foreground-base mb-md'>
              {t('sections.contact')}
            </h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-md'>
              {showMobileAndWebsite ? (
                <>
                  {phoneField}
                  <InputWrapper label={t('fields.mobile')} htmlFor={`${idPrefix}-mobile`}>
                    <PhoneInput
                      id={`${idPrefix}-mobile`}
                      value={formData.mobile}
                      maxLength={MOBILE_MAX_LENGTH}
                      onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder={t('placeholders.mobile')}
                    />
                  </InputWrapper>
                  {emailField}
                  <InputWrapper
                    label={t('fields.website')}
                    htmlFor={`${idPrefix}-website`}
                    isError={!!errors.website}
                    errorText={errors.website}
                  >
                    {/* Text, not type=url: the scheme is optional, which native URL validation refuses. */}
                    <Input
                      id={`${idPrefix}-website`}
                      type='text'
                      inputMode='url'
                      leftContent={<Icon name='link' />}
                      value={formData.website}
                      maxLength={WEBSITE_MAX_LENGTH}
                      onChange={e => setFormData({ ...formData, website: e.target.value })}
                      placeholder={t('placeholders.website')}
                    />
                    {/* Own id so InputWrapper doesn't clone the input's id onto the link. */}
                    {websiteHref ? (
                      <a
                        id={`${idPrefix}-website-link`}
                        href={websiteHref}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-sm text-info-text hover:opacity-80'
                      >
                        {t('links.openWebsite')}
                      </a>
                    ) : null}
                  </InputWrapper>
                </>
              ) : (
                <>
                  {emailField}
                  {phoneField}
                </>
              )}
            </div>
          </div>

          {/* Role */}
          <div className='border-t border-border-base pt-md'>
            <h3 className='text-base sm:text-lg font-semibold text-foreground-base mb-sm'>
              {t('sections.role')}
            </h3>
            <div className='flex flex-wrap gap-lg'>
              <label className='flex items-center gap-sm cursor-pointer'>
                <input
                  type='checkbox'
                  checked={formData.isSupplier}
                  onChange={e => setFormData({ ...formData, isSupplier: e.target.checked })}
                  className='w-4 h-4 text-primary-base border-border-base rounded focus:ring-border-base'
                />
                <span className='text-sm font-medium text-foreground-base'>
                  {t('fields.isSupplier')}
                </span>
              </label>
              <label className='flex items-center gap-sm cursor-pointer'>
                <input
                  type='checkbox'
                  checked={formData.isCustomer}
                  onChange={e => setFormData({ ...formData, isCustomer: e.target.checked })}
                  className='w-4 h-4 text-primary-base border-border-base rounded focus:ring-border-base'
                />
                <span className='text-sm font-medium text-foreground-base'>
                  {t('fields.isCustomer')}
                </span>
              </label>
              {!hideIsActive && (
                <label className='flex items-center gap-sm cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={formData.isActive}
                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                    className='w-4 h-4 text-primary-base border-border-base rounded focus:ring-border-base'
                  />
                  <span className='text-sm font-medium text-foreground-base'>
                    {t('fields.isActive')}
                  </span>
                </label>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
